<script lang="ts">
  // BJL-CHECK: Badge (used, bus-line badges), Spinner (used), Card (used in
  // Favorites/*ArrivalsCard), EmptyState. The subway bullets stay custom: a roundel is the
  // MTA's own mark, not a chip. EmptyState is unused: an empty slot here is a dashed outline
  // the width of the cards belonging in it, holding their shape and carrying the instruction
  // inline, where EmptyState reads as "nothing here" rather than "put something here".
  // See artifacts/deviations/FavoritesPage.md.
  import { Spinner } from '@byjackli-labs/ui/Spinner'
  import { Badge } from '@byjackli-labs/ui/Badge'
  import { Colors, getRouteColor, busBorough, BUS_BOROUGH_ORDER } from '../ui/colors'
  import { bulletFontPx, bulletText, Type } from '../ui/theme'
  import { useFavoritesStore } from '../store/favorites'
  import { routeVehicleCounts, useTransitStore } from '../store/transit'
  import { api, type StopWithRoutes } from '../services/api'
  import { storeSelect } from '../lib/store.svelte'
  import RouteBadge from '../components/RouteBadge.svelte'
  import RemoveButton from './Favorites/RemoveButton.svelte'
  import StopArrivalsCard from './Favorites/StopArrivalsCard.svelte'
  import BusStopArrivalsCard from './Favorites/BusStopArrivalsCard.svelte'

  const favorites = storeSelect(useFavoritesStore, (s) => s.favorites)
  const favoriteRoutes = storeSelect(useFavoritesStore, (s) => s.favoriteRoutes)
  const favoriteBusRoutes = storeSelect(useFavoritesStore, (s) => s.favoriteBusRoutes)
  const favoriteBusStops = storeSelect(useFavoritesStore, (s) => s.favoriteBusStops)
  const vehicleCounts = storeSelect(useTransitStore, routeVehicleCounts)

  let stopMeta = $state<Map<string, StopWithRoutes>>(new Map())
  let metaLoading = $state(false)

  // Station metadata, to resolve favorited ids → names. Subway stations and bus stops live
  // in separate favorite collections, so every entry in `favorites` is a subway station.
  $effect(() => {
    const ids = favorites.value
    if (ids.size === 0) return
    let live = true
    metaLoading = true
    api
      .getStopsWithRoutes()
      .then((d) => {
        if (!live) return
        // Key by the FAVORITED id, not the representative one: stations are served as
        // complexes, so a favorite saved pre-merge, or from the other division, matches only
        // on memberIds. The endpoints accept either, so the saved id stays usable.
        const map = new Map<string, StopWithRoutes>()
        for (const stop of d.stops) {
          for (const memberId of stop.memberIds ?? [stop.id]) {
            if (ids.has(memberId)) map.set(memberId, stop)
          }
        }
        stopMeta = map
      })
      .catch(() => {})
      .finally(() => { if (live) metaLoading = false })
    return () => { live = false }
  })

  // Live vehicle counts for the lines section
  // Every fleet's counts, not just the fleets this client receives (routeVehicleCounts).
  const vehicleCountByRoute = $derived(vehicleCounts.value)

  const favoriteRouteList = $derived([...favoriteRoutes.value])
  const subwayStopIds = $derived([...favorites.value])

  // Grouped by borough and sorted within each, so the section reads as per-borough rows
  // rather than one long list.
  const busLineGroups = $derived.by(() => {
    const groups = new Map<string, string[]>()
    for (const routeId of favoriteBusRoutes.value) {
      const borough = busBorough(routeId)
      const list = groups.get(borough) ?? []
      list.push(routeId)
      groups.set(borough, list)
    }
    return BUS_BOROUGH_ORDER.map((borough) => ({
      borough,
      routes: (groups.get(borough) ?? []).sort(),
    })).filter((g) => g.routes.length > 0)
  })
</script>

{#snippet sectionLabel(text: string)}
  <div
    style="color: {Colors.textMuted}; {Type.eyebrow}; padding: 18px 0 10px;"
  >{text}</div>
{/snippet}

<!-- An empty section is a dashed outline the width of the cards that would be there, not a
     centred illustration: it holds the shape of what belongs in the slot and says how to
     put something in it, which is the redesign's "+ Add favorite" affordance. -->
{#snippet emptyHint(text: string)}
  <div
    style="
      border: 1px dashed {Colors.borderStrong}; border-radius: 14px;
      padding: 14px; color: {Colors.textSecondary}; {Type.body};
    "
  >{text}</div>
{/snippet}

<div
  style="
    flex: 1; min-height: 0; display: flex; flex-direction: column;
    background-color: {Colors.ground};
  "
>
  <div style="flex: 1; overflow-y: auto; padding: 0 20px 32px;">
    <!-- Favorite Lines -->
    {@render sectionLabel('Lines')}

    {#if favoriteRouteList.length === 0}
      {@render emptyHint('No favorite lines yet. Tap ☆ on any route card.')}
    {:else}
      <div style="display: flex; flex-wrap: wrap; gap: 10px; padding-bottom: 16px;">
        {#each favoriteRouteList as routeId (routeId)}
          {@const count = vehicleCountByRoute[routeId] ?? 0}
          <div style="display: flex; flex-direction: column; align-items: center; gap: 3px;">
            <div style="position: relative; flex-shrink: 0;">
              <RemoveButton
                onRemove={() => useFavoritesStore.getState().toggleFavoriteRoute(routeId)}
              />
              <RouteBadge {routeId} size={44} />
            </div>
            <span style="color: {Colors.textMuted}; {Type.caption};">
              {count > 0 ? `${count} active` : '—'}
            </span>
          </div>
        {/each}
      </div>
    {/if}


    <!-- Favorite Bus Lines (grouped by borough) -->
    {@render sectionLabel('Bus Lines')}

    {#if busLineGroups.length === 0}
      {@render emptyHint(
        'No favorite bus lines yet. Tap a line inside any bus stop panel, then ☆.',
      )}
    {:else}
      <div style="display: flex; flex-direction: column; gap: 12px; padding-bottom: 16px;">
        {#each busLineGroups as { borough, routes } (borough)}
          <div>
            <div
              style="color: {Colors.textSecondary}; {Type.eyebrow}; margin-bottom: 6px;"
            >
              {borough} <span style="color: {Colors.textMuted};">· {routes.length}</span>
            </div>
            <div style="display: flex; flex-wrap: wrap; gap: 8px;">
              {#each routes as routeId (routeId)}
                <!-- Favorited bus line: a bjl Tag in the line's own MTA color. -->
                <div style="position: relative; flex-shrink: 0;">
                  <RemoveButton
                    onRemove={() => useFavoritesStore.getState().toggleFavoriteBusRoute(routeId)}
                  />
                  <Badge size="md" register="mono" colors={{ bg: getRouteColor(routeId), text: '#fff' }}>
                    {routeId}
                  </Badge>
                </div>
              {/each}
            </div>
          </div>
        {/each}
      </div>
    {/if}


    <!-- Favorite Subway Stations -->
    {@render sectionLabel('Subway Stations')}

    {#if subwayStopIds.length === 0}
      {@render emptyHint('No favorite stations yet. Tap ☆ on any station panel.')}
    {:else if metaLoading}
      <div style="display: flex; align-items: center; gap: 8px; color: {Colors.textMuted}; {Type.body};">
        <Spinner size="sm" label="Loading stations" /> Loading stations…
      </div>
    {:else}
      {#each subwayStopIds as id (id)}
        {@const meta = stopMeta.get(id)}
        <StopArrivalsCard
          stopId={id}
          stopName={meta?.name ?? id}
          onRemove={() => useFavoritesStore.getState().toggleFavorite(id)}
          onOpen={() =>
            useTransitStore.getState().requestStationOpen({
              id,
              name: meta?.name ?? id,
              routes: meta?.routes ?? [],
            })}
        />
      {/each}
    {/if}


    <!-- Favorite Bus Stops -->
    {@render sectionLabel('Bus Stops')}

    {#if favoriteBusStops.value.length === 0}
      {@render emptyHint('No favorite bus stops yet. Tap ☆ on any bus stop panel.')}
    {:else}
      {#each favoriteBusStops.value as stop (stop.id)}
        <BusStopArrivalsCard
          stopId={stop.id}
          stopName={stop.name}
          onRemove={() => useFavoritesStore.getState().toggleFavoriteBusStop(stop)}
          onOpen={() =>
            useTransitStore.getState().requestBusStopOpen({ id: stop.id, name: stop.name })}
        />
      {/each}
    {/if}
  </div>
</div>
